import { useEffect, useRef } from "react";
import type { ReactNode } from "react";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  /** aria-labelledby target id rendered by the caller's header. */
  labelledBy?: string;
  /** Tailwind max-width class for the panel, e.g. "max-w-md" / "max-w-2xl". */
  maxWidthClass?: string;
  children: ReactNode;
}

/**
 * Accessible overlay + centered panel. Backdrop click and Escape close it.
 * role=dialog, aria-modal. Full-bleed under sm; constrained above.
 */
export function Modal({
  open,
  onClose,
  labelledBy,
  maxWidthClass = "max-w-md",
  children,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  useEffect(() => {
    if (open && panelRef.current) {
      panelRef.current.focus();
    }
  }, [open]);

  if (!open) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-0 sm:p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        className={[
          "flex max-h-full w-full flex-col overflow-hidden bg-surface shadow-xl outline-none",
          "h-full rounded-none sm:h-auto sm:rounded-lg",
          maxWidthClass,
        ].join(" ")}
      >
        {children}
      </div>
    </div>
  );
}
